import { useContext, useEffect, useRef, useState } from "react";
import type { ContextType } from "react";
import axios from "axios";
import { message, Modal } from "antd";
import { AppContext } from "../../../store";
import { getAuthToken } from "../../../utils/common";
import { getSelectedSTObj } from "../../../utils/addressUtils";
import { cartUrl, cartItemQtyUpdateUrl, ACTIVITY_NO_PERMISSION_STATUS_VALUE,
  getActivityNoPermissionStatusLabel } from "../../../utils/constant";
import { ADD_ON_CART_MESSAGES as messages } from "./messages";

type CartData = ContextType<typeof AppContext>["cartData"];
interface CartLine {
  id: number;
  activityId: number;
  quantity: number;
  product: { limitQty?: number | null };
}
export type AddOnCartData = Omit<CartData, "activity"> & {
  activity: { productGroup: { cartItems: CartLine[] }[] };
};
interface CartResult { status: string; data: AddOnCartData }
interface Options {
  activityId: string | number;
  enabled: boolean;
  selectAllOnFirstLoad?: boolean;
}

function updateQuantity(item: CartLine, headers: Record<string, string>,
  pendingUpdates: Map<number, Promise<boolean>>) {
  const update = axios<{ status: string }>(cartItemQtyUpdateUrl, {
    method: "POST", headers,
    data: { id: item.id, activityId: item.activityId, quantity: item.product.limitQty },
  }).then(response => response.data.status === "success", () => false);
  pendingUpdates.set(item.id, update);
  void update.finally(() => pendingUpdates.delete(item.id));
  return update;
}

// 两端共用顺手买查询与自动限购调整，普通购物车继续走原请求链路。
export function useAddOnPurchaseCartList({ activityId, enabled, selectAllOnFirstLoad = true }: Options) {
  const { loginState, refreshCart, checkedCartItems, setCheckedCartItems, setCartData,
    setCartCount, activityPermissionMeta } = useContext(AppContext);
  const [loading, setLoading] = useState(false);
  const firstLoad = useRef(true);
  const sequence = useRef(0);
  const pendingUpdates = useRef(new Map<number, Promise<boolean>>());
  const selection = useRef(checkedCartItems);
  const selectAll = useRef(selectAllOnFirstLoad);
  selection.current = checkedCartItems;
  selectAll.current = selectAllOnFirstLoad;

  const customerId = loginState.user?.user?.userId ?? loginState.user?.user?.userName;
  // 首次全选只随活动或客户变化重置，凭证更新不应覆盖用户的勾选。
  useEffect(() => { firstLoad.current = true; }, [activityId, customerId]);

  useEffect(() => {
    if (!enabled || loginState.status !== "login") return;
    const requestSequence = ++sequence.current;
    const controller = new AbortController();
    const isCurrent = () => !controller.signal.aborted && sequence.current === requestSequence;
    const shipTo = getSelectedSTObj(loginState)?.accountNumber;
    const headers = { "Content-Type": "application/json", Authorization: getAuthToken() };
    const isFirstLoad = firstLoad.current;
    const shouldSelectAll = selectAll.current;
    const request = { activityId, shipTo, selectAll: isFirstLoad,
      cartIds: Array.from(selection.current) };
    const query = () => axios<CartResult>(cartUrl, {
      method: "POST", headers, data: request, signal: controller.signal,
    });
    setLoading(true);
    // 沿用普通购物车的刷新方式：保留当前内容，查询成功后整体替换。

    const load = async () => {
      // 已发出的数量写入不能当作已取消；新 ST 查询先等其完成，再读取实际购物车。
      await Promise.all(Array.from(pendingUpdates.current.values()));
      if (!isCurrent()) return;
      let result = (await query()).data;
      if (!isCurrent()) return;
      if (result.status !== "success") {
        const label = activityPermissionMeta.activityId === String(activityId)
          ? activityPermissionMeta.customerDataType : undefined;
        message.warning(result.status === ACTIVITY_NO_PERMISSION_STATUS_VALUE
          ? getActivityNoPermissionStatusLabel(label) : messages.loadFailed);
        return;
      }
      const items = result.data.activity.productGroup.flatMap(group => group.cartItems);
      const overLimit = items.filter(item => item.product.limitQty != null
        && item.quantity > item.product.limitQty);
      let adjustmentContent = "";
      if (overLimit.length > 0) {
        const updates = await Promise.all(overLimit.map(item => updateQuantity(item, headers, pendingUpdates.current)));
        if (!isCurrent()) return;
        const hasFailedAdjustment = updates.some(success => !success);
        if (updates.some(Boolean)) {
          adjustmentContent = hasFailedAdjustment ? messages.partiallyAdjusted : messages.adjusted;
        }
        if (hasFailedAdjustment) message.error(messages.updateFailed);
        // 本轮只调整一次，失败行保留服务端数量，避免自动更新与刷新相互触发。
        result = (await query()).data;
        if (!isCurrent()) return;
        if (result.status !== "success") throw new Error(messages.loadFailed);
      }
      setCartData(result.data);
      const ids = result.data.activity.productGroup.flatMap(group => group.cartItems.map(item => item.id));
      setCartCount(ids.length);
      if (isFirstLoad) {
        firstLoad.current = false;
        if (shouldSelectAll) setCheckedCartItems(new Set(ids));
      }
      if (adjustmentContent) {
        Modal.warning({
          title: messages.adjustedTitle,
          content: adjustmentContent,
          okText: messages.acknowledge,
          centered: true,
          className: "confirmModal",
        });
      }
    };
    void load().catch(() => {
      if (isCurrent()) message.error(messages.loadFailed);
    }).finally(() => {
      if (isCurrent()) setLoading(false);
    });
    return () => controller.abort();
    // 勾选和数量变更通过既有 refreshCart 触发，避免全选回写触发重复查询。
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activityId, enabled, loginState, refreshCart]);
  return enabled && loginState.status === "login" && loading;
}
